// General functions for retrieving product data via the API
window.ATOM = window.ATOM || {};
window.ATOM.products = window.ATOM.products || {};

class AtomProductHandler {
  constructor() {
    this.cache = window.ATOM.cache;

    // Debounce queues for batching requests
    this.stockQueue = new Set(); // Set of all unique SKUs currently queued
    this.stockPromises = []; // Array of {resolve, reject, skus} for each batch
    this.priceQueue = new Set(); // Set of all unique SKUs currently queued
    this.pricePromises = []; // Array of {resolve, reject, skus} for each batch
    this.stockTimer = null;
    this.priceTimer = null;
    this.debounceDelay = 100; // 100ms debounce delay
  }

  async stock(skus) {
    if (typeof skus == 'string') skus = [skus];

    // Check we have a warehouse selected
    if (!window.ATOM.customer?.warehouse?.code) {
      console.error('Unable to retrieve stock details due to no selected warehouse');
      return {};
    }

    // Return a promise that will be resolved when the batch request completes
    return new Promise((resolve, reject) => {
      // Add SKUs to the queue
      skus.forEach((sku) => this.stockQueue.add(sku));

      // Store the promise with its associated SKUs
      this.stockPromises.push({ resolve, reject, skus: [...skus] });

      // Clear existing timer and set a new one
      if (this.stockTimer) clearTimeout(this.stockTimer);
      this.stockTimer = setTimeout(() => this.processStockQueue(), this.debounceDelay);
    });
  }

  async price(skus) {
    if (typeof skus == 'string') skus = [skus];

    // Check we have an approved customer cos we can't check the API without one
    if (!window.ATOM.customer?.isApproved) {
      console.debug('Unable to retrieve pricing data — customer not approved');
      return {};
    }

    // Return a promise that will be resolved when the batch request completes
    return new Promise((resolve, reject) => {
      // Add SKUs to the queue
      skus.forEach((sku) => this.priceQueue.add(sku));

      // Store the promise with its associated SKUs
      this.pricePromises.push({ resolve, reject, skus: [...skus] });

      // Clear existing timer and set a new one
      if (this.priceTimer) clearTimeout(this.priceTimer);
      this.priceTimer = setTimeout(() => this.processPriceQueue(), this.debounceDelay);
    });
  }

  async processStockQueue() {
    if (this.stockQueue.size === 0) return;

    const skus = Array.from(this.stockQueue.keys());
    const promises = [...this.stockPromises]; // Capture all pending promises
    const punchout = window.ATOM.customer.metafields?.punchout?.is_punchout ?? false;

    // Clear the queues
    this.stockQueue.clear();
    this.stockPromises.length = 0;

    try {
      const response = await window.ATOM.api({
        type: 'stock',
        warehouse: window.ATOM.customer.warehouse.code,
        punchout: punchout,
        skus: skus,
      });

      if (!response) throw new Error('Error retrieving stock details');

      // Resolve each promise with the full response for its requested SKUs
      promises.forEach(({ resolve, skus: requestedSkus }) => {
        const result = {};
        requestedSkus.forEach((sku) => {
          result[sku] = response[sku] || null;
        });
        resolve(result);
      });
    } catch (error) {
      // Reject all promises on error
      promises.forEach(({ reject }) => reject(error));
    }

    this.stockTimer = null;
  }

  async processPriceQueue() {
    if (this.priceQueue.size === 0) return;

    const skus = Array.from(this.priceQueue.keys());
    const promises = [...this.pricePromises]; // Capture all pending promises

    // Clear the queues
    this.priceQueue.clear();
    this.pricePromises.length = 0;

    try {
      const response = await window.ATOM.api({ type: 'price', skus: skus });

      if (!response) throw new Error('Error retrieving price data');

      // Check if we need to add the warehouse surcharge
      // Skip surcharge for contracted/quoted items — those are customer-negotiated prices
      if (window.ATOM.customer?.warehouse?.surcharge && parseFloat(window.ATOM.customer.warehouse.surcharge) > 0) {
        const surcharge = 1 + parseFloat(window.ATOM.customer.warehouse.surcharge);
        Object.entries(response).forEach(([sku, productData]) => {
          if (productData?.type === 'contract' || productData?.type === 'quoted') return;
          productData.price = productData.price * surcharge;
          productData.volume_pricing =
            productData.volume_pricing &&
            productData.volume_pricing.map((v) => {
              v.p = v.p * surcharge;
              return v;
            });
        });
      }

      // Resolve each promise with the full response for its requested SKUs
      promises.forEach(({ resolve, skus: requestedSkus }) => {
        const result = {};
        requestedSkus.forEach((sku) => {
          result[sku] = response[sku] || null;
        });
        resolve(result);
      });
    } catch (error) {
      // Reject all promises on error
      console.debug('Price error:', error, skus);
      promises.forEach(({ reject }) => reject(error));
    }

    this.priceTimer = null;
  }

  // Force immediate processing of any queued requests
  flushQueues() {
    if (this.stockTimer) {
      clearTimeout(this.stockTimer);
      this.stockTimer = null;
    }
    if (this.priceTimer) {
      clearTimeout(this.priceTimer);
      this.priceTimer = null;
    }

    // Process any remaining queued requests
    if (this.stockQueue.size > 0) {
      this.processStockQueue();
    }
    if (this.priceQueue.size > 0) {
      this.processPriceQueue();
    }
  }
}

// Instantiate the class
window.ATOM.products = new AtomProductHandler();

// Register a handler to clear the cache when the warehouse is changed
document.addEventListener('atom:warehouse:selected', async () => {
  // Check we have the cache object
  if (!window.ATOM.cache) return;

  // Check if the cached data matches this warehouse
  const product = await window.ATOM.cache.getFirst('stock');
  if (!product?.warehouse || product.warehouse == window.ATOM.customer?.warehouse?.code) return;

  // This is a different warehouse so clear the stock cache
  window.ATOM.cache.clear('stock');
});
